import React, { FC, useEffect, useState } from 'react'
import { Col, Descriptions, Row, Spin } from 'antd'
import { StyledCollapse } from '../styles'
import { modelApi } from '../../../../api'
import RBarChart from '../../../../components/Charts/RBarChart'

import { convertNumberToCommaSeperated } from '../../../../utils'
import { IBatterySocCountsEnriched } from '../../../../types/battery'
import BatteryStatesBarChart from './BatteryStatesBarChart'

type IDescriptionKeys =
  | 'essCyclesYearly'
  | 'totalCycleNumber'
  | 'totalStoredEnergyMwh'
  | 'averageStoragedEnergyMwhPerYear'
  | 'averageSoc'
  | 'averageChargingCRate'
  | 'averageDischargeCRate'

type Props = {
  wideCharts: boolean

  fileIdBatteryStates: string
  fileIdSocCount: string
  fileIdSocChargeCount: string
  fileIdSocDischargeCount: string

  essCyclesYearly: number
  totalCycleNumber: number
  totalStoredEnergyMwh: number
  averageStoragedEnergyMwhPerYear: number
  averageSoc: number
  averageChargingCRate: number
  averageDischargeCRate: number
}

const storageStatistics: { title: string; dataKey: IDescriptionKeys }[] = [
  { title: 'ESS Cycles (Yearly) ', dataKey: 'essCyclesYearly' },
  { title: 'Total Cycle Number', dataKey: 'totalCycleNumber' },
  { title: 'Total Stored Energy (MWh)', dataKey: 'totalStoredEnergyMwh' },
  {
    title: 'Average Storaged Energy per Year(MWh/Year)',
    dataKey: 'averageStoragedEnergyMwhPerYear',
  },
  { title: 'Average SOC', dataKey: 'averageSoc' },
  { title: 'Average Charging C-Rate', dataKey: 'averageChargingCRate' },
  { title: 'Average Discharge C-Rate', dataKey: 'averageDischargeCRate' },
]

const BatterySocBarCharts: FC<Props> = ({
  wideCharts,
  fileIdBatteryStates,
  fileIdSocCount,
  fileIdSocChargeCount,
  fileIdSocDischargeCount,
  ...descriptionItems
}) => {
  const [isLoading, setLoading] = useState(true)
  const [socCounts, setSocCounts] = useState<IBatterySocCountsEnriched>([])
  const [socChargeCounts, setChargeSocCounts] = useState<IBatterySocCountsEnriched>([])
  const [socDischargeCounts, setSocDischargeCounts] = useState<IBatterySocCountsEnriched>([])

  async function fetchChartData() {
    function enrichValues(state: number, values: number[], idx: number) {
      return { state, value: values[idx] }
    }

    try {
      setLoading(true)
      const [socCounts, socChargeCounts, socDischargeCounts] = await Promise.all([
        modelApi.fetchSocCounts(fileIdSocCount),
        modelApi.fetchSocCounts(fileIdSocChargeCount),
        modelApi.fetchSocCounts(fileIdSocDischargeCount),
      ])

      const states = Object.values(socCounts.state)
      const socValues = Object.values(socCounts.value)
      const socChargeValues = Object.values(socChargeCounts.value)
      const socDischargeValues = Object.values(socDischargeCounts.value)

      const [enrichedSocCounts, enrichedSocChargeCounts, enrichedSocDischargeCounts] = [
        socValues,
        socChargeValues,
        socDischargeValues,
      ].map(
        (socData) =>
          states.map((state, idx) =>
            enrichValues(state, socData, idx),
          ) as IBatterySocCountsEnriched,
      )

      setSocCounts(enrichedSocCounts)
      setChargeSocCounts(enrichedSocChargeCounts)
      setSocDischargeCounts(enrichedSocDischargeCounts)
    } catch (error) {
      setSocCounts([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchChartData()
  }, [])

  return (
    <StyledCollapse className='mb-24' defaultActiveKey={1}>
      <StyledCollapse.Panel header='Battery' key={1}>
        <Spin spinning={isLoading}>
          <Row className='mb-36' gutter={40}>
            <Col span={wideCharts ? 24 : 12}>
              <RBarChart
                syncId='battery'
                barCategoryGap={0}
                data={socCounts}
                dataKey='state'
                bars={[
                  {
                    dataKey: 'value',
                    fill: 'blue',
                  },
                ]}
                yAxis={{
                  domain: [0, 100],
                  label: {
                    value: 'Time %',
                    position: 'insideLeft',
                    angle: -90,
                  },
                }}
              />
            </Col>

            <Col span={wideCharts ? 24 : 12}>
              <RBarChart
                height={145}
                syncId='battery'
                barCategoryGap={0}
                data={socChargeCounts}
                dataKey='state'
                bars={[
                  {
                    dataKey: 'value',
                    fill: 'green',
                  },
                ]}
                yAxis={{ domain: [0, 100] }}
              />
              <RBarChart
                height={145}
                syncId='battery'
                barCategoryGap={0}
                data={socDischargeCounts}
                dataKey='state'
                bars={[
                  {
                    dataKey: 'value',
                    fill: 'red',
                  },
                ]}
                yAxis={{ domain: [0, 100] }}
              />
            </Col>
          </Row>
        </Spin>

        <Descriptions className='mb-48' layout='vertical' bordered>
          {storageStatistics.map(({ title, dataKey }) => (
            <Descriptions.Item key={dataKey} label={title}>
              {convertNumberToCommaSeperated(descriptionItems[dataKey] || 0)}
            </Descriptions.Item>
          ))}
        </Descriptions>

        <BatteryStatesBarChart id={fileIdBatteryStates} />
      </StyledCollapse.Panel>
    </StyledCollapse>
  )
}

export default BatterySocBarCharts
